import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { LoginService } from '../login.service';
import { AdministrationService } from '../administration.service';
import { PatientsSidebarService } from './patients-sidebar.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { SEARCH_DEBOUNCE_MS } from '../pagination';

export interface SearchParams {
  id: string;
  first: string;
  last: string;
  phone: string;
  noawl: boolean;
}

export interface AddPatientParams {
  id: string;
  first: string;
  last: string;
  phone: string;
}

// Patients nav (/patsearch + /dashboard); mode-aware, talks to PatientsComponent via PatientsSidebarService, carrying intent through pending* fields when off /patsearch.
@Component({
  selector: 'app-patients-nav',
  templateUrl: './patients-sidebar.component.html',
  styleUrls: ['./patients-sidebar.component.scss'],
  standalone: false,
})
export class PatientsNavComponent implements OnInit, OnDestroy {
  isAWL = false;

  id = '';
  first = '';
  last = '';
  phone = '';
  idp = 1;
  invalidi = '';
  invalidf = '';
  invalidl = '';

  // ── New sidebar UI state ─────────────────────────────────
  patientSearchOpen = false;
  activeNavItem:
    | 'dashboard'
    | 'patient-search'
    | 'todays-list'
    | 'search-exams'
    | 'reports'
    | null = null;

  private destroy$ = new Subject<void>();
  // Keystrokes land here and are debounced, so typing fires one request instead of one per character.
  private searchInput$ = new Subject<string>();
  // Mirrors the service so the template can read it synchronously.
  searching = false;

  constructor(
    public loginService: LoginService,
    private administrationService: AdministrationService,
    private router: Router,
    private sidebarService: PatientsSidebarService
  ) {}

  get newSidebar(): boolean {
    return this.loginService.newSidebar;
  }

  // True when PatientsComponent is mounted, so actions can fire inline.
  private get onPatsearch(): boolean {
    return this.router.url.startsWith('/patsearch');
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  togglePatientSearch(): void {
    this.patientSearchOpen = !this.patientSearchOpen;
    this.activeNavItem = this.patientSearchOpen ? 'patient-search' : null;
    this.sidebarService.activeNavChange$.next(this.activeNavItem);
    if (!this.onPatsearch) {
      this.sidebarService.pendingNavItem = 'patient-search';
      this.router.navigate(['/patsearch']);
    }
  }

  setActiveNav(
    item: 'dashboard' | 'patient-search' | 'todays-list' | 'search-exams' | 'reports'
  ): void {
    this.patientSearchOpen = false;
    this.activeNavItem = item;
    this.sidebarService.activeNavChange$.next(item);
    if (!this.onPatsearch) {
      this.sidebarService.pendingNavItem = item;
      this.router.navigate(['/patsearch']);
    }
  }

  private syncActiveFromRoute(url: string): void {
    if (url.startsWith('/patsearch')) {
      const pending = this.sidebarService.pendingNavItem;
      if (pending) {
        // Arriving from another route (e.g. /dashboard) with an explicit nav intent — restore it.
        this.activeNavItem = pending as typeof this.activeNavItem;
        this.patientSearchOpen = pending === 'patient-search';
        this.sidebarService.pendingNavItem = null; // consume
      } else if (this.activeNavItem === null) {
        // Fresh init with no pending intent — default to today's list.
        this.activeNavItem = 'todays-list';
      }
    } else if (url.startsWith('/dashboard')) {
      this.activeNavItem = 'dashboard';
      this.patientSearchOpen = false;
    }
  }

  ngOnInit(): void {
    // Set initial highlight from the current URL
    this.syncActiveFromRoute(this.router.url);

    // Keep in sync when navigating (e.g. returning to /patsearch from another page)
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => this.syncActiveFromRoute(e.urlAfterRedirects));

    // Receive isAWL state pushed by PatientsComponent
    this.sidebarService.isAWL$
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => (this.isAWL = val));

    // Receive in-flight state so the Search button can't be re-fired mid-request
    this.sidebarService.searching$
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => (this.searching = val));

    this.searchInput$
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.emitDebouncedSearch());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Keystroke entry point: sanitise the ID, then hand off to the debounced stream.
  trysearch() {
    this.id = this.id.replace(/\-/g, '_').replace(/\W/g, '').replace(/_/g, '-');
    this.searchInput$.next(`${this.id}|${this.first}|${this.last}|${this.phone}`);
  }

  // Runs once typing settles - the decision logic trysearch() used to run on every keystroke.
  // Goes through emitSearch() rather than search$ directly: off /patsearch there is no
  // PatientsComponent subscribed, so a direct emit is dropped and nothing happens.
  private emitDebouncedSearch() {
    if (!this.isSearchDisabled()) {
      this.emitSearch(!this.loginService.ishospital);
      return;
    }
    if (this.isAWL && this.id.length === 0 && this.first.length === 0 && this.last.length === 0) {
      this.emitSearch(false);
    }
  }

  // Button gate: the content rule plus an in-flight check, so the button can't be spammed while a search is pending.
  // Kept separate from isSearchDisabled() so a request in flight never blocks the debounced auto-search.
  get isSearchButtonDisabled(): boolean {
    return this.searching || this.isSearchDisabled();
  }

  // Search gate: at least one field with 2+ characters. Shared by auto-search and the Search button.
  isSearchDisabled(): boolean {
    return !(
      this.id.trim().length > 1 ||
      this.first.trim().length > 1 ||
      this.last.trim().length > 1 ||
      this.phone.trim().length > 1
    );
  }

  clear() {
    this.id = '';
    this.first = '';
    this.last = '';
    this.phone = '';
    this.sidebarService.clear$.next();
  }

  requestAddPatient() {
    this.invalidi = '';
    if (this.id.length < 7) {
      this.invalidi = 'is-invalid';
      return;
    }
    this.invalidf = '';
    if (this.first === '') {
      this.invalidf = 'is-invalid';
      return;
    }
    this.invalidl = '';
    if (this.last === '') {
      this.invalidl = 'is-invalid';
      return;
    }
    const payload: AddPatientParams = {
      id: this.id,
      first: this.first,
      last: this.last,
      phone: this.phone,
    };
    if (this.onPatsearch) {
      this.sidebarService.addPatient$.next(payload);
    } else {
      this.sidebarService.pendingAddPatient = payload;
      this.router.navigate(['/patsearch']);
    }
  }

  redirectAdministration(type: string) {
    this.administrationService.redirect(type);
  }

  emitSearch(noawl: boolean): void {
    const payload: SearchParams = {
      id: this.id,
      first: this.first,
      last: this.last,
      phone: this.phone,
      noawl,
    };
    if (this.onPatsearch) {
      this.sidebarService.search$.next(payload);
    } else {
      this.sidebarService.pendingSearch = payload;
      this.router.navigate(['/patsearch']);
    }
  }

  emitAwl(): void {
    const payload: AddPatientParams = {
      id: this.id,
      first: this.first,
      last: this.last,
      phone: this.phone,
    };
    if (this.onPatsearch) {
      this.sidebarService.awl$.next(payload);
    } else {
      this.sidebarService.pendingAwl = payload;
      this.router.navigate(['/patsearch']);
    }
  }

  emitOpenEsearch(isReports: boolean): void {
    if (!this.onPatsearch) {
      // PatientsComponent is not yet mounted — store the value for pickup on ngOnInit
      this.sidebarService.pendingOpenEsearch = isReports;
      this.router.navigate(['/patsearch']);
    } else {
      this.sidebarService.openEsearch$.next(isReports);
    }
  }
}
